import { Autocomplete, type Item } from '@ads/components-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getAutocompleteSearchMessage } from '@/shared/utils/getAutocompleteSearchMessage';
import { useClassSearchDropdown } from '../../../hooks/useClassSearchDropdown';

export interface TurmaAutocompleteProps {
  /** ID (`classStudentId`) da turma selecionada; `''` quando nenhuma está selecionada. */
  readonly value: string;
  /** Rótulo da turma selecionada (para exibir a seleção corrente). */
  readonly selectedLabel?: string;
  /** Notifica o novo ID de turma e seu rótulo (`''` ao limpar). */
  readonly onChange: (classId: string, className: string) => void;
  readonly label: string;
  readonly placeholder: string;
}

/**
 * Autocomplete de turmas com busca por texto (debounce + mínimo de 3 caracteres),
 * espelhando o `ClassAutocomplete` de student-management. Consome
 * {@link useClassSearchDropdown} (endpoint real `class-students` do Kubb).
 */
export function TurmaAutocomplete({
  value,
  selectedLabel: selectedLabelProp = '',
  onChange,
  label,
  placeholder,
}: TurmaAutocompleteProps) {
  const { t: tCommon } = useTranslation('common');
  const [search, setSearch] = useState('');
  const [selectedLabel, setSelectedLabel] = useState(selectedLabelProp);

  const { classes, isQuerySuccess } = useClassSearchDropdown(search);

  const items = classes.map((c): Item<string> => ({ label: c.name, value: c.id }));
  const selectedItem: Item<string> = value
    ? { label: selectedLabel, value }
    : { label: '', value: '' };

  const notFoundMessage = getAutocompleteSearchMessage(
    search.length,
    isQuerySuccess,
    items.length,
    {
      minChars: tCommon('search.minChars'),
      searching: tCommon('search.searching'),
      notFound: tCommon('search.notFound'),
    },
  );

  return (
    <Autocomplete<string>
      label={label}
      placeholder={placeholder}
      size="md"
      list={items}
      custom={{ input: '!w-full' }}
      selectedItem={selectedItem}
      {...(notFoundMessage !== undefined ? { notFoundMessage } : {})}
      onChange={e => setSearch(e.target.value)}
      handleChange={item => {
        onChange(item.value ?? '', item.label ?? '');
        setSelectedLabel(item.label ?? '');
        setSearch('');
      }}
      handleClearQuery={() => {
        onChange('', '');
        setSelectedLabel('');
        setSearch('');
      }}
    />
  );
}
